import * as React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BudgetCard } from '@/components/budget/BudgetCard';
import { useLeaveSharedBudget } from '@/hooks/useShares';
import { toast } from 'sonner';
import type { Budget } from '@/types';

interface SharedWithMeSectionProps {
  budgets: Budget[];
}

/**
 * Lists all budgets shared with the current user, with a Leave action,
 * or an empty state when nothing has been shared.
 */
export function SharedWithMeSection({ budgets }: SharedWithMeSectionProps) {
  const leaveBudget = useLeaveSharedBudget();
  const [leavingId, setLeavingId] = React.useState<string | null>(null);

  const handleLeave = async (budget: Budget) => {
    setLeavingId(budget.id);
    try {
      await leaveBudget.mutateAsync(budget.id);
      toast.success(`You left "${budget.name}"`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to leave budget';
      toast.error(message);
    } finally {
      setLeavingId(null);
    }
  };

  return (
    <section>
      <h2 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Shared with me ({budgets.length})
      </h2>

      {budgets.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            No budgets have been shared with you yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {budgets.map((budget) => (
            <div key={budget.id} className="space-y-1">
              <BudgetCard
                budget={budget}
                titleEditable={false}
                showChildSummary
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleLeave(budget)}
                  loading={leavingId === budget.id}
                >
                  Leave shared budget
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
